/**
 * Configuration constants for repository dropzone functionality.
 * Centralizes all magic numbers and thresholds for easier maintenance.
 */

/**
 * Maximum number of files allowed in a single upload batch.
 * Set to 100 to balance user needs with browser memory constraints and
 * API rate limits. Higher values risk timeout and memory issues.
 */
export const MAX_REPOSITORY_UPLOAD_FILES = 100;

/**
 * Number of files that triggers a confirmation dialog before upload.
 * Set to 20 to warn users about potentially long uploads without being
 * overly cautious. Most users can upload 20 files in reasonable time.
 */
export const LARGE_UPLOAD_THRESHOLD = 20;

/**
 * Cap progress at 99% during upload phase to prevent "stuck at 100%" perception
 * while the file is being persisted to the database (shown as separate "persisting" state).
 * This prevents UI confusion where users see 100% but the operation isn't complete.
 */
export const UPLOAD_PROGRESS_MAX_WHILE_UPLOADING = 99;

/**
 * How often Filestack's uploader reports batch progress (milliseconds).
 * Set to 100ms to provide smooth visual feedback without overwhelming React's
 * state update queue. Lower values cause performance issues, higher values
 * make progress appear choppy.
 */
export const PROGRESS_THROTTLE_MS = 100;

/**
 * Maximum filename length to display before truncation (pixels).
 * Set to 200px to fit most filenames in the upload progress panel while
 * preventing layout overflow from extremely long names.
 */
export const MAX_FILENAME_DISPLAY_WIDTH = 200;

/**
 * Sentry feature tag for all repository dropzone error tracking.
 */
export const REPOSITORY_DROPZONE_SENTRY_FEATURE = 'repository-dropzone' as const;
